import { useQuery } from "@tanstack/react-query";
import { admin, type AdminRole } from "@/lib/admin";
import { useSession } from "./useSession";
import { isAdminVerified } from "./useIsAdmin";

// Returns the caller's admin tier: 'super_admin' | 'admin' | 'viewer' | null.
// Null = not an admin (or session not yet ready).
// Only queries when the JWT-level admin gate has cleared AAL2, so we don't fire
// a 403'd edge function call before MFA completes.
export function useAdminRole(): { role: AdminRole | null; loading: boolean } {
  const { session, loading: sessionLoading } = useSession();
  const enabled = isAdminVerified(session);

  const q = useQuery({
    queryKey: ["admin", "myRole"],
    queryFn: () => admin.myRole(),
    enabled,
    staleTime: 60_000,
  });

  if (sessionLoading || (enabled && q.isLoading)) {
    return { role: null, loading: true };
  }
  if (!enabled) return { role: null, loading: false };
  return { role: q.data?.role ?? null, loading: false };
}

// Permission helpers for UI gating. Match the edge function's ROLE_PERMISSIONS
// so the UI hides actions the backend would reject anyway.
export function canMutateUsers(role: AdminRole | null): boolean {
  return role === "admin" || role === "super_admin";
}

export function canManageAdmins(role: AdminRole | null): boolean {
  return role === "super_admin";
}

export function canManageCoupons(role: AdminRole | null): boolean {
  return role === "super_admin";
}

export function canDeleteUsers(role: AdminRole | null): boolean {
  return role === "super_admin";
}
